/**
 * THE APP, AS DATA (client half).
 *
 * This file is the whole client-side answer to "what does this product mount?".
 * Adding a kernel feature is one line in `appComposition`; removing it is
 * deleting that line.
 *
 * Only migrated features appear here. Everything else is still wired by hand,
 * on purpose: adoption is incremental, one feature at a time. See
 * infra/plugin-kernel/ADOPTION.md.
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { History } from "@fleet/icons";
import type { Accessor, Component } from "solid-js";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { PowerSyncDrizzleDB } from "@comandero/powersync";
import {
  composition,
  type Composition,
  type PlatformTarget,
  plugged,
  type Plugin,
} from "@fleet/kernel";
import { deepLinkingPlugin } from "@/plugins/deep-linking";
import { UNDO_BINDING, UNDO_COMMAND } from "@/lib/shortcuts/defaults";
import type { Command, KeyBinding } from "@fleet/shortcuts";

/** A navigation entry, in this app's own vocabulary. */
export interface AppNavItem {
  id: string;
  to: string;
  order: number;
  label: MessageDescriptor;
  icon: Component<{ class?: string }>;
}

/**
 * EVERYTHING A PLUGIN CAN HAND THIS APP'S UI.
 *
 * The kernel is generic over this union and never looks inside it. Adding a
 * third kind of contribution is a line here, not a change to the kernel.
 *
 * The point is the COUPLING: a contribution only exists while the plugin that
 * makes it is mounted, so a feature skipped for lack of a capability takes its
 * menu entry and its keyboard shortcut with it.
 */
export type AppContribution =
  | { kind: "nav"; item: AppNavItem }
  | { kind: "command"; command: Command; binding?: KeyBinding };
import { browserDelivery } from "@fleet/export";
import {
  type ActivityHistoryService,
  activityPlugin,
  mapApiActivityRow,
  PLATFORM_ACTIVITY_HISTORY,
  PLATFORM_POWERSYNC,
  type PowerSyncService,
} from "@fleet/plugins/activity";
import { exportPlugin, tauriDelivery } from "@fleet/plugins/export";
import { adminPlugin } from "@fleet/plugins/admin";
import { adminRoute } from "@/routes/_app/admin";
import { adminIndexRoute } from "@/routes/_app/admin.index";
import { adminUsersRoute } from "@/routes/_app/admin.users";
import { adminFlagsRoute } from "@/routes/_app/admin.flags";
import { adminMetricsRoute } from "@/routes/_app/admin.metrics";
import { adminIntegrationsRoute } from "@/routes/_app/admin.integrations";
import { adminAuditLogRoute } from "@/routes/_app/admin.audit-log";
import { adminStorageRoute } from "@/routes/_app/admin.storage";
import { adminThemeAuditRoute } from "@/routes/_app/admin.theme-audit";
import { adminThemesRoute } from "@/routes/_app/admin.themes";
import { adminFontsRoute } from "@/routes/_app/admin.fonts";
import { adminEmailRoute } from "@/routes/_app/admin.email";
import { adminEmailNewRoute } from "@/routes/_app/admin.email.new";
import { adminEmailEditRoute } from "@/routes/_app/admin.email.$templateId";
import { adminEmailCampaignsRoute } from "@/routes/_app/admin.email.campaigns";
import { activityRoute } from "@/routes/_app/activity";
import { api } from "@/lib/api";
import { activityFilter, activityRowMatches } from "@/features/activity/shift-filter";

/** What the host has to hand the kernel. Resolved from the provider tree. */
export interface HostServices {
  /** i18n, supplied by the host: a plugin never reaches into a Solid context. */
  translate: (message: MessageDescriptor) => string;
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  /** Reported to the user by the host, never by a plugin. */
  onHistoryError: () => void;
}

/**
 * Publishes the ambient services every plugin may depend on. One per host, not
 * one per feature: a new plugin that needs PowerSync declares
 * `requires: [PLATFORM_POWERSYNC]` and gets it.
 */
function platformPlugin(host: HostServices): Plugin<void, AppContribution> {
  return {
    id: "platform",
    provides: [PLATFORM_POWERSYNC, PLATFORM_ACTIVITY_HISTORY],
    data(ctx) {
      const powerSync: PowerSyncService = {
        db: host.db,
        drizzle: host.drizzle,
        userId: host.userId,
      };
      ctx.provide(PLATFORM_POWERSYNC, powerSync);

      const history: ActivityHistoryService = {
        fetchOlder: async (before, limit, filter) => {
          try {
            const { data, error } = await api.GET("/api/activity", {
              params: {
                query: {
                  before,
                  limit,
                  registerSessionId: filter.registerSessionId ?? null,
                },
              },
            });
            if (error || !data) {
              host.onHistoryError();
              return null;
            }
            return {
              rows: data.items.map(mapApiActivityRow),
              hasMore: Boolean(data.nextBefore),
            };
          } catch {
            host.onHistoryError();
            return null;
          }
        },
      };
      ctx.provide(PLATFORM_ACTIVITY_HISTORY, history);
    },
  };
}

const adminRoutes = [
  adminRoute.addChildren([
    adminIndexRoute,
    adminUsersRoute,
    adminFlagsRoute,
    adminMetricsRoute,
    adminIntegrationsRoute,
    adminAuditLogRoute,
    adminStorageRoute,
    adminThemeAuditRoute,
    adminThemesRoute,
    adminFontsRoute,
    adminEmailRoute,
    adminEmailNewRoute,
    adminEmailEditRoute,
    adminEmailCampaignsRoute,
  ]),
] as const;

export const pluginRoutes = [activityRoute, ...adminRoutes] as const;

export const NO_HOST: HostServices = {
  translate: (s) => (typeof s === "string" ? s : ""),
  db: () => null,
  drizzle: () => null,
  userId: () => undefined,
  onHistoryError: () => {
    throw new Error("composition read without a host: routes pass only");
  },
};

export function appComposition(
  host: HostServices,
  target: PlatformTarget,
): Composition<AppContribution> {
  return composition<AppContribution>({ name: "comandero", target }, [
    plugged(platformPlugin(host), undefined),
    plugged(
      activityPlugin<AppContribution>(),
      {
        contributes: [
          {
            kind: "nav",
            item: {
              id: "activity",
              to: "/activity",
              order: 30,
              label: msg`Activity`,
              icon: History,
            },
          },
          // Global undo replays the compensating write this feature records.
          { kind: "command", command: UNDO_COMMAND, binding: UNDO_BINDING },
        ],
        // POS variant: the feed is narrowed by register session. The plugin
        // forwards this without interpreting it.
        filter: activityFilter,
        matchesFilter: activityRowMatches,
      },
      [activityRoute],
    ),
    // Feature #8: admin routes and contributions.
    plugged(adminPlugin, undefined, adminRoutes),
    // Feature #2: almost all of `export/` is a plain library. Only the
    // delivery is a plugin, because WHICH delivery to use is a capability
    // decision — and this is the only place in the app that makes it.
    plugged(exportPlugin, { browser: browserDelivery, fileSystem: tauriDelivery }),
    plugged(deepLinkingPlugin, { translate: host.translate }),
  ]);
}
